/**
 * Feb 2026 — Self-hosted crash reporter.
 *
 * Two entry points:
 *  1. `reportFrontendError({...})` — called from the React ErrorBoundary
 *     when a component-tree crash happens.
 *  2. `installGlobalErrorHandlers()` — called once at app boot to wire
 *     `window.error` + `unhandledrejection` so uncaught runtime errors
 *     (like the TDZ crash that shipped Feb 2026 on /customers) still
 *     land in the same feed.
 *
 * We deliberately keep this dependency-free (no axios) so a broken
 * bundle can't take the reporter down with it. Uses `fetch` and stays
 * silent on any failure.
 */

// Persistent per-tab id so admins can group repeated crashes into one
// "session affected" count on the backend.
const SESSION_KEY = 'fe_error_session_id';
let _sessionId = null;

function getSessionId() {
  if (_sessionId) return _sessionId;
  try {
    let existing = sessionStorage.getItem(SESSION_KEY);
    if (!existing) {
      existing = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, existing);
    }
    _sessionId = existing;
  } catch {
    _sessionId = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
  return _sessionId;
}

// Client-side dedupe: if the same fingerprint fires 5x in 10 seconds
// (typical of a render loop), stop transmitting until the user
// navigates. The server also rate-limits by IP as a second layer.
const _recentFingerprints = new Map(); // fp -> { count, first }
function shouldSend(fp) {
  const now = Date.now();
  const rec = _recentFingerprints.get(fp) || { count: 0, first: now };
  if (now - rec.first > 10_000) {
    // Fresh 10-second window.
    _recentFingerprints.set(fp, { count: 1, first: now });
    return true;
  }
  rec.count += 1;
  _recentFingerprints.set(fp, rec);
  return rec.count <= 5;
}

function makeFingerprint(message, stack) {
  const stackHead = stack ? stack.split('\n').slice(0, 2).join('|') : '';
  // Cheap non-crypto hash — same shape server computes but any stable
  // string works since it's only used for the client-side dedupe.
  let h = 0;
  const s = `${message}\n${stackHead}`;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return String(h);
}

export function reportFrontendError(payload) {
  try {
    const message = (payload?.message || 'Unknown error').toString().slice(0, 2000);
    const stack = payload?.stack ? String(payload.stack).slice(0, 8000) : undefined;
    const componentStack = payload?.component_stack
      ? String(payload.component_stack).slice(0, 8000)
      : undefined;
    const fp = makeFingerprint(message, stack || '');
    if (!shouldSend(fp)) return;

    const body = {
      message,
      stack,
      component_stack: componentStack,
      route: window.location?.pathname,
      url: window.location?.href,
      user_agent: navigator?.userAgent?.slice(0, 500),
      session_id: getSessionId(),
      kind: payload?.kind || 'react',
    };

    const base = process.env.REACT_APP_BACKEND_URL || '';
    const url = `${base}/api/frontend-errors`;
    // Prefer sendBeacon so the request survives a navigation, but fall
    // back to fetch(keepalive) when Beacon isn't available or the
    // payload is too large.
    const json = JSON.stringify(body);
    let sent = false;
    try {
      if (navigator?.sendBeacon && json.length < 60_000) {
        const blob = new Blob([json], { type: 'application/json' });
        sent = navigator.sendBeacon(url, blob);
      }
    } catch { /* ignore */ }
    if (!sent) {
      const token = (() => { try { return localStorage.getItem('token'); } catch { return null; } })();
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: json,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => { /* silent */ });
    }
  } catch { /* never let the reporter crash the app */ }
}

let _handlersInstalled = false;
export function installGlobalErrorHandlers() {
  if (_handlersInstalled) return;
  _handlersInstalled = true;

  // Uncaught synchronous errors (including TDZ, "x is not a function",
  // and anything else that escapes React's boundary because it fired
  // during module init or an async callback).
  window.addEventListener('error', (event) => {
    // Ignore ResizeObserver noise & script-load errors from third-
    // parties that don't include a message (they're near-useless).
    const msg = event?.message || '';
    if (!msg || /ResizeObserver loop/i.test(msg)) return;
    reportFrontendError({
      message: msg,
      stack: event?.error?.stack,
      kind: 'window',
    });
  });

  // Unhandled promise rejections — usually axios errors that nobody
  // caught, sometimes a component that awaited a broken import.
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    reportFrontendError({
      message: (reason && (reason.message || reason.toString())) || 'Unhandled promise rejection',
      stack: reason && reason.stack,
      kind: 'unhandledrejection',
    });
  });
}
